import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './utils/AuthContext';
import { ProfilesProvider } from './utils/ProfilesContext';
import { AuthActionPage } from './components/AuthActionPage.tsx';
import { MotionConfig } from 'motion/react';
import { consumeUpdateReload, createUpdateReload } from './utils/startup';

const resumingUpdate = consumeUpdateReload(() => sessionStorage,
  (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type ?? '');
const reloadForUpdate = createUpdateReload(() => sessionStorage, () => window.location.reload());

// Keyboard modality for the focus ring (see :focus-visible in index.css): on
// once the keyboard is used to move around, off again on any tap or click.
const NAV_KEYS = new Set(['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
document.addEventListener('keydown', e => { if (NAV_KEYS.has(e.key)) document.documentElement.dataset.keyboard = ''; }, true);
document.addEventListener('pointerdown', () => { delete document.documentElement.dataset.keyboard; }, true);

// Reload an already controlled page for a new release, not for the first
// installation claiming a fresh page that's already running the current bundle.
if (import.meta.env.MODE !== 'native' && 'serviceWorker' in navigator) {
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Do not interrupt a one-use verification/recovery action after it is sent.
    // Leaving this page naturally loads the newly activated app version.
    if (window.location.pathname === '/auth-action') return;
    const wasControlled = hadController;
    hadController = !!navigator.serviceWorker.controller;
    if (!wasControlled || !hadController) return;
    reloadForUpdate();
  });
  // iOS PWAs don't proactively check for a new SW when re-foregrounded — they
  // rely on the browser's natural schedule which can be many hours. Calling
  // update() on every visibility change forces an immediate network fetch of
  // the SW file so a new build activates within seconds of the app being opened.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker.ready.then(reg => reg.update()).catch(() => {});
    }
  });
}

// A lazily loaded chunk (a tab or import modal) can fail to fetch — offline on
// first visit, or a tab still running a release whose chunk hashes are gone.
// React caches the failed import, so the error boundary's "Try Again" can
// never recover it; a reload can. Once per session, so a chunk that is truly
// unreachable lands on the error boundary instead of reloading forever.
window.addEventListener('vite:preloadError', event => {
  if (window.location.pathname === '/auth-action') return;
  try {
    if (sessionStorage.getItem('chunk-reload')) return;
    sessionStorage.setItem('chunk-reload', '1');
  } catch { return; }
  event.preventDefault();
  reloadForUpdate();
});

// Firebase auth action links (password reset, email verification) are directed
// to talismantravel.app/auth-action — render the branded handler instead of the
// full app when the path matches.
const isAuthAction = window.location.pathname === '/auth-action';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Every Motion animation follows the system's Reduce Motion setting:
        movement and scale drop out, opacity fades stay. Only two animations
        honoured it before. */}
    <MotionConfig reducedMotion="user">
    {isAuthAction ? (
      <AuthActionPage />
    ) : (
      <AuthProvider>
        <ProfilesProvider>
          <App resumingUpdate={resumingUpdate} />
        </ProfilesProvider>
      </AuthProvider>
    )}
    </MotionConfig>
  </StrictMode>,
);
